import { useEffect, useState } from "react";
import { AudioLines, Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Why VoiceLens", href: "#why" },
  { label: "Who It's For", href: "#who" },
  { label: "My Story", href: "#story" },
  { label: "Showcase", href: "#showcase" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "glass-panel border-b border-[#00D4FF]/15" : "bg-transparent"
      }`}
    >
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8"
        aria-label="Main navigation"
      >
        <a href="#main-content" className="flex items-center gap-3" data-testid="nav-logo-link">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#2563EB] to-[#00D4FF] shadow-[0_0_18px_rgba(0,212,255,0.4)]">
            <AudioLines className="h-5 w-5 text-white" strokeWidth={2.4} aria-hidden="true" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-white">
            VoiceLens <span className="text-[#00D4FF]">AI</span>
          </span>
        </a>

        <ul className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-[#00D4FF]"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href="/?view=app"
            data-testid="nav-open-app-button"
            className="hidden items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(37,99,235,0.45)] transition-all duration-300 hover:bg-[#1D4ED8] hover:shadow-[0_0_28px_rgba(0,212,255,0.5)] sm:inline-flex"
          >
            Open App
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#00D4FF]/25 text-slate-200 lg:hidden"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="glass-panel border-t border-[#00D4FF]/15 lg:hidden" data-testid="nav-mobile-panel">
          <ul className="space-y-1 px-6 py-4">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  className="block rounded-lg px-3 py-3 text-base font-medium text-slate-200 hover:bg-[#00D4FF]/10"
                  onClick={() => setOpen(false)}
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href="/?view=app"
                data-testid="nav-mobile-open-app-button"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-5 py-3.5 text-base font-semibold text-white"
              >
                Open VoiceLens AI
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
